import React from "react";

const content = {
  en: {
    title: "Terms of Service",
    updated: "Last updated: March 2026",
    sections: [
      {
        title: "1. Acceptance of Terms",
        paragraphs: [
          "By accessing or using Vapor (the \"Service\"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.",
          "Vapor is operated by Vapor Technology Co., Ltd.. We reserve the right to update these terms at any time. Continued use of the Service after changes constitutes acceptance of the revised terms.",
        ],
      },
      {
        title: "2. Description of Service",
        paragraphs: [
          "Vapor is a free, open-source, end-to-end encrypted messaging application. The Service enables users to send and receive encrypted messages, files, and media through a decentralized network without requiring personal information such as phone numbers or email addresses.",
        ],
      },
      {
        title: "3. Account Registration",
        paragraphs: [
          "To use Vapor, you will be assigned a unique Account ID upon creating an account. No personal information is required for registration. You are responsible for maintaining the security of your Account ID and recovery phrase.",
          "You must not create accounts for the purpose of violating these terms or for any unlawful activity.",
        ],
      },
      {
        title: "4. User Conduct",
        paragraphs: [
          "You agree not to use the Service to:",
        ],
        list: [
          "Transmit any content that is unlawful, harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable",
          "Impersonate any person or entity, or falsely represent your affiliation with any person or entity",
          "Distribute malware, viruses, or any other harmful software",
          "Interfere with or disrupt the Service or servers connected to the Service",
          "Engage in any activity that violates applicable local, national, or international laws",
          "Exploit the Service for unauthorized commercial purposes",
        ],
      },
      {
        title: "5. Privacy and Data",
        paragraphs: [
          "Vapor is designed with privacy as its core principle. We do not collect, store, or process personal data, message content, metadata, IP addresses, or contact lists. All messages are end-to-end encrypted and routed through a decentralized onion-routing network.",
          "Because we have no access to user data, we cannot recover lost accounts or messages. Please refer to our Privacy Policy for detailed information.",
        ],
      },
      {
        title: "6. Intellectual Property",
        paragraphs: [
          "Vapor is open-source software released under applicable open-source licenses. The Vapor name, logo, and brand assets are the property of Vapor Technology Co., Ltd. and may not be used without permission except as described in our Media Kit guidelines.",
        ],
      },
      {
        title: "7. Third-Party Services",
        paragraphs: [
          "The Service may contain links to third-party websites or services that are not owned or controlled by Vapor. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services.",
        ],
      },
      {
        title: "8. Disclaimer of Warranties",
        paragraphs: [
          "The Service is provided on an \"AS IS\" and \"AS AVAILABLE\" basis. Vapor makes no warranties, expressed or implied, regarding the Service's reliability, availability, or fitness for a particular purpose.",
          "We do not guarantee that the Service will be uninterrupted, secure, or error-free. You use the Service at your own risk.",
        ],
      },
      {
        title: "9. Limitation of Liability",
        paragraphs: [
          "To the maximum extent permitted by applicable law, Vapor Technology Co., Ltd. shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of, or inability to use, the Service.",
        ],
      },
      {
        title: "10. Termination",
        paragraphs: [
          "You may stop using the Service at any time by deleting your account through the app settings. We reserve the right to suspend or terminate access to the Service for users who violate these terms, without prior notice.",
        ],
      },
      {
        title: "11. Governing Law",
        paragraphs: [
          "These Terms shall be governed by and construed in accordance with the laws of the Republic of China (Taiwan), without regard to its conflict of law provisions.",
        ],
      },
      {
        title: "12. Contact",
        paragraphs: [
          "If you have any questions about these Terms of Service, please contact us at:",
        ],
        email: "service@getvapor.org",
      },
    ],
  },
  zhTW: {
    title: "服務條款",
    updated: "最後更新：2026 年 3 月",
    sections: [
      {
        title: "1. 條款接受",
        paragraphs: [
          "存取或使用 Vapor（以下簡稱「本服務」）即表示您同意受本服務條款之約束。若您不同意這些條款，請勿使用本服務。",
          "Vapor 由 Vapor Technology Co., Ltd. 營運。我們保留隨時更新這些條款的權利。在條款變更後繼續使用本服務，即構成對修訂條款的接受。",
        ],
      },
      {
        title: "2. 服務說明",
        paragraphs: [
          "Vapor 是一款免費、開放原始碼、端對端加密的通訊應用程式。本服務讓使用者能夠透過去中心化網路發送和接收加密訊息、檔案和媒體，無需提供手機號碼或電子郵件等個人資訊。",
        ],
      },
      {
        title: "3. 帳戶註冊",
        paragraphs: [
          "使用 Vapor 時，系統會在您建立帳戶時分配一組獨特的帳戶 ID。註冊無需任何個人資訊。您有責任維護帳戶 ID 和恢復助記詞的安全。",
          "您不得為違反本條款或從事任何違法活動之目的而建立帳戶。",
        ],
      },
      {
        title: "4. 使用者行為",
        paragraphs: [
          "您同意不使用本服務從事以下行為：",
        ],
        list: [
          "傳送任何違法、有害、威脅、辱罵、騷擾、誹謗或其他令人不快的內容",
          "冒充任何個人或實體，或虛假陳述與任何個人或實體的關聯",
          "散布惡意軟體、病毒或任何其他有害程式",
          "干擾或破壞本服務或連接到本服務的伺服器",
          "從事任何違反適用的地方、國家或國際法律之活動",
          "將本服務用於未經授權的商業目的",
        ],
      },
      {
        title: "5. 隱私與資料",
        paragraphs: [
          "Vapor 以隱私保護為核心設計原則。我們不蒐集、儲存或處理個人資料、訊息內容、後設資料、IP 位址或聯絡人名單。所有訊息均經過端對端加密，並透過去中心化的洋蔥路由網路傳送。",
          "由於我們無法存取使用者資料，因此無法恢復遺失的帳戶或訊息。詳細資訊請參閱我們的隱私權政策。",
        ],
      },
      {
        title: "6. 智慧財產權",
        paragraphs: [
          "Vapor 是根據適用的開放原始碼授權條款發布的開放原始碼軟體。Vapor 名稱、標誌及品牌資產為 Vapor Technology Co., Ltd. 所有，未經許可不得使用，除非媒體素材指南中另有說明。",
        ],
      },
      {
        title: "7. 第三方服務",
        paragraphs: [
          "本服務可能包含指向非 Vapor 擁有或控制的第三方網站或服務之連結。我們對任何第三方網站或服務的內容、隱私政策或做法不承擔任何控制權或責任。",
        ],
      },
      {
        title: "8. 免責聲明",
        paragraphs: [
          "本服務以「現況」及「可用」的基礎提供。Vapor 不對本服務的可靠性、可用性或特定用途之適用性做出任何明示或暗示的保證。",
          "我們不保證本服務不會中斷、安全或無錯誤。您使用本服務需自行承擔風險。",
        ],
      },
      {
        title: "9. 責任限制",
        paragraphs: [
          "在適用法律允許的最大範圍內，Vapor Technology Co., Ltd. 不對因您使用或無法使用本服務而導致的任何間接、附帶、特殊、後果性或懲罰性損害承擔責任。",
        ],
      },
      {
        title: "10. 終止",
        paragraphs: [
          "您可以隨時透過應用程式設定刪除帳戶以停止使用本服務。我們保留在不事先通知的情況下，暫停或終止違反本條款之使用者存取本服務的權利。",
        ],
      },
      {
        title: "11. 準據法",
        paragraphs: [
          "本條款應受中華民國（臺灣）法律管轄並據其解釋，不考慮其法律衝突條款。",
        ],
      },
      {
        title: "12. 聯繫方式",
        paragraphs: [
          "如果您對本服務條款有任何疑問，請透過以下方式與我們聯繫：",
        ],
        email: "service@getvapor.org",
      },
    ],
  },
};

const Terms = ({ locale }) => {
  const key = locale === "zh-TW" ? "zhTW" : locale;
  const t = content[key] || content.en;

  return (
    <section className="legal-page">
      <div className="container">
        <div className="legal-header">
          <h1>{t.title}</h1>
          <p className="legal-updated">{t.updated}</p>
        </div>
        <div className="legal-content">
          {t.sections.map((sec, i) => (
            <div className="legal-section" key={i}>
              <h2>{sec.title}</h2>
              {sec.paragraphs.map((p, j) => (
                <p key={j}>{p}</p>
              ))}
              {sec.list && (
                <ul>
                  {sec.list.map((item, k) => (
                    <li key={k}>{item}</li>
                  ))}
                </ul>
              )}
              {sec.email && (
                <span className="legal-email">{sec.email}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Terms;
